import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient } from "../lib/api";

const ThemeCtx = createContext(null);
// "Youth" = the existing playful default themes (blue_ocean / green_land / neutral).
// "Adult" themes (conservatory / studio_pro / editorial_jazz) added 2026-05-06 — free for all users.
// Premium novelty themes (sunset / midnight / arcade) retired 2026-05-06.
export const YOUTH_THEMES   = ["blue_ocean", "green_land", "neutral"];
export const ADULT_THEMES   = ["conservatory", "studio_pro", "editorial_jazz"];
export const ALL_THEMES     = [...YOUTH_THEMES, ...ADULT_THEMES];

export const THEME_META = {
  // ─── Youth (playful defaults) ─────────────────────────────────────
  blue_ocean:     { label: "Blue Ocean",      group: "youth", swatch: "linear-gradient(135deg, #0077B6, #00B4D8)" },
  green_land:     { label: "Green Land",      group: "youth", swatch: "linear-gradient(135deg, #2D6A4F, #95D5B2)" },
  neutral:        { label: "Neutral",         group: "youth", swatch: "linear-gradient(135deg, #6c757d, #adb5bd)" },
  // ─── Adult (designed for grown-up musicians) ────────────────────
  conservatory:   { label: "Conservatory",    group: "adult", swatch: "linear-gradient(135deg, #15233E 0%, #15233E 60%, #D4AF37 100%)" },
  studio_pro:     { label: "Studio Pro",      group: "adult", swatch: "linear-gradient(135deg, #0a0a0a 0%, #0a0a0a 60%, #00E0B8 100%)" },
  editorial_jazz: { label: "Editorial Jazz",  group: "adult", swatch: "linear-gradient(135deg, #6F1D1B 0%, #6F1D1B 60%, #B08D33 100%)" },
};

export function ThemeProvider({ children, userTheme, userPremium, userBg }) {
  const [theme, setTheme] = useState(() => {
    const cached = localStorage.getItem("ptp_theme");
    return cached && ALL_THEMES.includes(cached) ? cached : "blue_ocean";
  });

  useEffect(() => {
    if (userTheme && ALL_THEMES.includes(userTheme)) setTheme(userTheme);
  }, [userTheme]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("ptp_theme", theme);
  }, [theme]);

  // Apply custom background image on the root element (Premium only)
  useEffect(() => {
    const root = document.documentElement;
    if (userPremium && userBg) {
      root.style.setProperty("--app-bg-image", `url("${userBg}")`);
      root.classList.add("has-custom-bg");
    } else {
      root.style.removeProperty("--app-bg-image");
      root.classList.remove("has-custom-bg");
    }
  }, [userPremium, userBg]);

  const changeTheme = useCallback(async (t) => {
    if (!ALL_THEMES.includes(t)) return;
    setTheme(t);
    try {
      await apiClient.put("/me/theme", { theme: t });
    } catch (e) { console.warn("Theme persist failed", e); }
  }, []);

  const value = useMemo(() => ({ theme, setTheme: changeTheme }), [theme, changeTheme]);

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
